import React, { useRef } from "react";
import { Box, Typography, Container, Button, Chip } from "@mui/material";
import { motion, useMotionValue, useTransform, useSpring, useScroll, useReducedMotion } from "framer-motion";
import Header1 from "./Header/Header1";
import CertificateImage from "./Icon/Startup_India_Certificate.png";
import Startup_Karnataka_Registration from "./Footer/Startup_Karnataka_Registration.png";
import Footer from "./Footer/Footer";

/* ─────────────────────── FONTS + GLOBAL ─────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700;12..96,800&family=DM+Sans:wght@300;400;500;600&display=swap');

    @keyframes ach-streak {
      0%   { transform: translateX(-120%) skewX(-22deg); opacity: 0; }
      15%  { opacity: 1; }
      100% { transform: translateX(220%)  skewX(-22deg); opacity: 0; }
    }
    @keyframes ach-float {
      0%,100% { transform: translateY(0px)   rotate(0deg);   }
      33%     { transform: translateY(-14px)  rotate(.4deg);  }
      66%     { transform: translateY(-6px)   rotate(-.3deg); }
    }
    @keyframes ach-orb {
      0%,100% { opacity:.28; transform: scale(1);    }
      50%     { opacity:.55; transform: scale(1.12); }
    }
    @keyframes ach-grain {
      0%,100%{ transform:translate(0,0) }
      20%   { transform:translate(-2px,2px) }
      40%   { transform:translate(2px,-1px) }
      60%   { transform:translate(-1px,3px) }
      80%   { transform:translate(1px,-2px) }
    }
    .ach-grain {
      position:fixed; inset:-30%; width:160%; height:160%;
      opacity:.028; pointer-events:none; z-index:0;
      background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.72' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
      animation: ach-grain 8s steps(2) infinite;
    }
  `}</style>
);

/* ─────────────────────── PARTICLE FIELD ─────────────────────────── */
const PARTICLE_SEED = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  cx: (Math.sin(i * 2.39996) * 0.5 + 0.5) * 100,
  cy: (Math.cos(i * 1.61803) * 0.5 + 0.5) * 100,
  r:  (Math.sin(i * 3.71) * 0.5 + 0.5) * 2.8 + 1.2,
  dur: 4 + (i % 5) * 0.9,
  dly: -(i * 0.38),
}));

const ParticleField = () => {
  const reduced = useReducedMotion();
  return (
    <Box aria-hidden sx={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0, overflow:"hidden" }}>
      {PARTICLE_SEED.map((p) => (
        <Box
          key={p.id}
          component={reduced ? "div" : motion.div}
          {...(!reduced && {
            animate: { y:[0, -22, 0], opacity:[0.18, 0.7, 0.18] },
            transition: { duration:p.dur, delay:p.dly, repeat:Infinity, ease:"easeInOut" },
          })}
          sx={{
            position:"absolute",
            left:`${p.cx}%`, top:`${p.cy}%`,
            width:`${p.r}px`, height:`${p.r}px`,
            borderRadius:"50%",
            background:"#1E90FF",
            boxShadow:`0 0 ${p.r*4}px rgba(30,144,255,.55)`,
          }}
        />
      ))}
      {/* Light streaks */}
      {[
        { top:"18%", dur:7,  dly:0   },
        { top:"52%", dur:9,  dly:-3  },
        { top:"76%", dur:11, dly:-6  },
      ].map((s, i) => (
        <Box key={`streak-${i}`} sx={{
          position:"absolute", top:s.top, left:0, right:0,
          height:"1px", overflow:"hidden", pointerEvents:"none",
        }}>
          <Box sx={{
            position:"absolute", inset:0,
            background:"linear-gradient(90deg,transparent 0%,rgba(30,144,255,.28) 50%,transparent 100%)",
            width:"35%", height:"100%",
            animation:`ach-streak ${s.dur}s linear infinite`,
            animationDelay:`${s.dly}s`,
          }}/>
        </Box>
      ))}
    </Box>
  );
};

/* ─────────────────────── 3D TILT CARD ───────────────────────────── */
const TiltCard = ({ children, intensity = 12, ...props }) => {
  const ref  = useRef(null);
  const reduced = useReducedMotion();
  const rotX = useSpring(useMotionValue(0), { stiffness: 140, damping: 22 });
  const rotY = useSpring(useMotionValue(0), { stiffness: 140, damping: 22 });

  const onMove = (e) => {
    if (reduced || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx   = rect.left + rect.width  / 2;
    const cy   = rect.top  + rect.height / 2;
    const dx   = (e.clientX - cx) / (rect.width  / 2);
    const dy   = (e.clientY - cy) / (rect.height / 2);
    rotY.set( dx * intensity);
    rotX.set(-dy * intensity);
  };
  const onLeave = () => { rotX.set(0); rotY.set(0); };

  return (
    <Box
      ref={ref}
      component={motion.div}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 900 }}
      {...props}
    >
      {children}
    </Box>
  );
};

/* ─────────────────────── SECTION LABEL ──────────────────────────── */
const SectionLabel = ({ text }) => (
  <Box sx={{ display:"inline-flex", alignItems:"center", gap:"8px", mb:"16px" }}>
    <Box sx={{ width:6, height:6, borderRadius:"50%", background:"#1E90FF", boxShadow:"0 0 10px #1E90FF" }}/>
    <Typography sx={{
      fontFamily:"'DM Sans',sans-serif",
      fontSize:"clamp(.6rem,.8vw,.72rem)",
      fontWeight:700,
      color:"rgba(30,144,255,.85)",
      letterSpacing:".14em",
      textTransform:"uppercase",
    }}>
      {text}
    </Typography>
  </Box>
);

/* ─────────────────────── GLOW ORBS (section-level) ─────────────── */
const GlowOrb = ({ top, left, right, bottom, w = 500, h = 500, opacity = .08 }) => (
  <Box aria-hidden sx={{
    position:"absolute",
    ...(top    !== undefined && { top    }),
    ...(left   !== undefined && { left   }),
    ...(right  !== undefined && { right  }),
    ...(bottom !== undefined && { bottom }),
    width:`${w}px`, height:`${h}px`,
    borderRadius:"50%",
    background:`radial-gradient(circle, rgba(30,144,255,${opacity}) 0%, transparent 70%)`,
    filter:"blur(80px)",
    pointerEvents:"none",
    animation:"ach-orb 10s ease-in-out infinite",
  }}/>
);

/* ─────────────────────── CERTIFICATE FRAME ─────────────────────── */
const CertFrame = ({ src, alt, float = true }) => (
  <Box sx={{ position:"relative" }}>
    {/* Ambient glow behind frame */}
    <Box aria-hidden sx={{
      position:"absolute", inset:"-18%",
      borderRadius:"50%",
      background:"radial-gradient(ellipse, rgba(30,144,255,.18) 0%, transparent 65%)",
      filter:"blur(40px)",
      pointerEvents:"none",
    }}/>

    <TiltCard intensity={8}>
      <Box
        sx={{
          position:"relative",
          borderRadius:"28px",
          overflow:"hidden",
          border:"1px solid rgba(30,144,255,.22)",
          background:"rgba(8,16,36,.82)",
          backdropFilter:"blur(24px)",
          WebkitBackdropFilter:"blur(24px)",
          boxShadow:
            "0 0 0 1px rgba(30,144,255,.08)," +
            "0 24px 80px rgba(0,0,0,.7)," +
            "0 0 60px rgba(30,144,255,.12)," +
            "inset 0 1px 0 rgba(255,255,255,.06)",
          animation: float ? "ach-float 7s ease-in-out infinite" : "none",
          // Reflection strip at top
          "&::before": {
            content:'""',
            position:"absolute", top:0, left:"12%", right:"12%", height:"1px",
            background:"linear-gradient(90deg,transparent,rgba(30,144,255,.55),transparent)",
            zIndex:2,
          },
        }}
      >
        {/* Shimmer overlay */}
        <Box aria-hidden sx={{
          position:"absolute", inset:0, zIndex:1, pointerEvents:"none",
          background:"linear-gradient(135deg,rgba(255,255,255,.04) 0%,transparent 50%,rgba(30,144,255,.04) 100%)",
        }}/>
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            display:"block",
            width:"100%",
            height:"auto",
            position:"relative",
            zIndex:0,
          }}
        />
      </Box>
    </TiltCard>

    {/* Decorative corner marks */}
    {[
      { top:"-8px",    left:"-8px",  btm:"auto", rt:"auto"  },
      { top:"-8px",    right:"-8px", btm:"auto", lt:"auto"  },
      { bottom:"-8px", left:"-8px",  tp:"auto",  rt:"auto"  },
      { bottom:"-8px", right:"-8px", tp:"auto",  lt:"auto"  },
    ].map((pos, i) => (
      <Box key={i} aria-hidden sx={{
        position:"absolute",
        top:    pos.top    ?? "auto",
        bottom: pos.bottom ?? "auto",
        left:   pos.left   ?? "auto",
        right:  pos.right  ?? "auto",
        width:"16px", height:"16px",
        borderTop:    (i < 2)  ? "1.5px solid rgba(30,144,255,.5)" : "none",
        borderBottom: (i >= 2) ? "1.5px solid rgba(30,144,255,.5)" : "none",
        borderLeft:   (i === 0 || i === 2) ? "1.5px solid rgba(30,144,255,.5)" : "none",
        borderRight:  (i === 1 || i === 3) ? "1.5px solid rgba(30,144,255,.5)" : "none",
      }}/>
    ))}
  </Box>
);

/* ─────────────────────── RECOGNITION META ──────────────────────── */
const RecogMeta = ({ label, title, body, children }) => (
  <Box sx={{ display:"flex", flexDirection:"column", justifyContent:"center", height:"100%" }}>
    <SectionLabel text={label}/>
    <Typography component="h2" sx={{
      fontFamily:"'Bricolage Grotesque',sans-serif",
      fontWeight:800,
      fontSize:"clamp(2rem,4.5vw,3.8rem)",
      lineHeight:1.06,
      letterSpacing:"clamp(-1.2px,-0.05em,-2px)",
      color:"rgba(255,255,255,.97)",
      mb:"18px",
      wordBreak:"break-word",
    }}>
      {title}
    </Typography>
    <Typography sx={{
      fontFamily:"'DM Sans',sans-serif",
      fontSize:"clamp(.9rem,1.3vw,1.08rem)",
      lineHeight:1.88,
      color:"rgba(185,210,255,.65)",
      mb:"32px",
      maxWidth:"520px",
    }}>
      {body}
    </Typography>
    {children}
  </Box>
);

/* ─────────────────────── BADGE CHIP ────────────────────────────── */
const StatusBadge = ({ text }) => (
  <Box sx={{
    display:"inline-flex", alignItems:"center", gap:"7px",
    px:"14px", py:"7px", borderRadius:"999px",
    background:"rgba(30,144,255,.08)",
    border:"1px solid rgba(30,144,255,.22)",
    width:"fit-content", mb:"28px",
  }}>
    <Box sx={{
      width:7, height:7, borderRadius:"50%", background:"#1E90FF",
      boxShadow:"0 0 8px rgba(30,144,255,.9)",
    }}/>
    <Typography sx={{
      fontFamily:"'DM Sans',sans-serif",
      fontSize:"clamp(.62rem,.82vw,.72rem)",
      fontWeight:700, color:"rgba(30,144,255,.88)",
      letterSpacing:".1em", textTransform:"uppercase",
    }}>
      {text}
    </Typography>
  </Box>
);

/* ─────────────────────── MAGAZINE CARD (Silicon India) ─────────── */
const MagazineFeature = () => (
  <motion.div
    initial={{ opacity:0, y:28 }}
    whileInView={{ opacity:1, y:0 }}
    viewport={{ once:true, margin:"-60px" }}
    transition={{ duration:.7, ease:[.22,1,.36,1] }}
  >
    <Box sx={{
      position:"relative",
      borderRadius:"28px",
      overflow:"hidden",
      background:"linear-gradient(135deg,rgba(12,22,48,.96) 0%,rgba(6,10,22,.98) 100%)",
      border:"1px solid rgba(30,144,255,.2)",
      backdropFilter:"blur(24px)",
      boxShadow:
        "0 24px 80px rgba(0,0,0,.65)," +
        "0 0 0 1px rgba(30,144,255,.06)," +
        "inset 0 1px 0 rgba(255,255,255,.05)",
      p:{ xs:"28px 22px", sm:"36px 32px", md:"44px 40px", lg:"52px 48px" },
    }}>
      {/* Top accent bar */}
      <Box sx={{
        position:"absolute", top:0, left:"8%", right:"8%", height:"1.5px",
        background:"linear-gradient(90deg,transparent,rgba(30,144,255,.65),transparent)",
      }}/>

      {/* Corner glow */}
      <Box aria-hidden sx={{
        position:"absolute", top:-60, right:-60,
        width:200, height:200, borderRadius:"50%",
        background:"radial-gradient(circle,rgba(30,144,255,.16),transparent 70%)",
        filter:"blur(25px)", pointerEvents:"none",
      }}/>

      <Box sx={{
        display:"grid",
        gridTemplateColumns:{ xs:"1fr", md:"1fr 1fr" },
        gap:{ xs:"32px", md:"48px" },
        alignItems:"center",
      }}>
        {/* Left — editorial content */}
        <Box>
          {/* Publication badge */}
          <Box sx={{
            display:"inline-flex", alignItems:"center", gap:"10px",
            px:"14px", py:"8px", borderRadius:"10px", mb:"24px",
            background:"rgba(255,255,255,.04)",
            border:"1px solid rgba(255,255,255,.08)",
          }}>
            <Box sx={{
              width:24, height:24, borderRadius:"6px",
              background:"linear-gradient(135deg,#1E90FF,#60A5FA)",
              display:"flex", alignItems:"center", justifyContent:"center",
              flexShrink:0,
            }}>
              <Typography sx={{ fontSize:".55rem", fontWeight:700, color:"#fff", lineHeight:1 }}>SI</Typography>
            </Box>
            <Typography sx={{
              fontFamily:"'DM Sans',sans-serif",
              fontSize:"clamp(.68rem,.9vw,.78rem)",
              fontWeight:600, color:"rgba(255,255,255,.6)",
              letterSpacing:".05em",
            }}>
              Silicon India · 2025
            </Typography>
          </Box>

          {/* Feature label */}
          <Box sx={{
            display:"inline-flex", alignItems:"center", gap:"6px",
            px:"10px", py:"5px", borderRadius:"999px", mb:"16px",
            background:"rgba(30,144,255,.1)",
            border:"1px solid rgba(30,144,255,.2)",
            ml:"10px",
          }}>
            <Typography sx={{
              fontFamily:"'DM Sans',sans-serif",
              fontSize:".6rem", fontWeight:700,
              color:"rgba(30,144,255,.9)",
              letterSpacing:".12em", textTransform:"uppercase",
            }}>
              Cover Feature
            </Typography>
          </Box>

          <Typography sx={{
            display:"block",
            fontFamily:"'Bricolage Grotesque',sans-serif",
            fontWeight:800,
            fontSize:"clamp(1.6rem,3.5vw,3rem)",
            lineHeight:1.08,
            letterSpacing:"clamp(-1px,-0.05em,-1.8px)",
            color:"rgba(255,255,255,.96)",
            mb:"16px",
            wordBreak:"break-word",
          }}>
            Top 10 AI Startups from 
            <Box component="span" sx={{ color:"#1E90FF" }}> Bangalore</Box>
            <br/>— 2025
          </Typography>

          <Typography sx={{
            fontFamily:"'DM Sans',sans-serif",
            fontSize:"clamp(.86rem,1.2vw,1rem)",
            lineHeight:1.85, mb:"28px",
            color:"rgba(185,210,255,.62)",
            maxWidth:"440px",
          }}>
            Featured among emerging innovators for building impactful
            technology products and contributing to the startup ecosystem —
            a testament to engineering excellence and enterprise AI vision.
          </Typography>

          {/* Meta row */}
          <Box sx={{
            display:"flex", flexWrap:"wrap",
            gap:"20px", mb:"30px",
            pt:"20px", borderTop:"1px solid rgba(255,255,255,.06)",
          }}>
            {[
              { label:"Category",   val:"AI & Technology"   },
              { label:"Edition",    val:"Cover — 2025"      },
              { label:"Publisher",  val:"Silicon India"      },
            ].map((m) => (
              <Box key={m.label}>
                <Typography sx={{ fontFamily:"'DM Sans',sans-serif", fontSize:".62rem", fontWeight:600, color:"rgba(255,255,255,.3)", letterSpacing:".08em", textTransform:"uppercase", mb:"3px" }}>
                  {m.label}
                </Typography>
                <Typography sx={{ fontFamily:"'Bricolage Grotesque',sans-serif", fontWeight:700, fontSize:".9rem", color:"rgba(255,255,255,.82)" }}>
                  {m.val}
                </Typography>
              </Box>
            ))}
          </Box>

          <Button
            variant="contained"
            component={motion.a}
            href="https://startup.siliconindia.com/magazine/cover-feature/ai-startups-from-bangalore-2025-anthrasync-cid-26995.html"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale:1.03 }}
            whileTap={{ scale:.97 }}
            sx={{
              background:"linear-gradient(90deg,#1E90FF,#42A5FF)",
              color:"#fff",
              textTransform:"none",
              fontFamily:"'DM Sans',sans-serif",
              fontWeight:700,
              fontSize:"clamp(.84rem,1vw,.94rem)",
              px:"24px", py:"11px",
              borderRadius:"12px",
              boxShadow:"0 10px 30px rgba(30,144,255,.28)",
              "&:hover": { background:"linear-gradient(90deg,#42A5FF,#1E90FF)", boxShadow:"0 14px 40px rgba(30,144,255,.4)" },
            }}
          >
            Read the Feature →
          </Button>
        </Box>

        {/* Right — editorial magazine article card */}
        <Box>
          <TiltCard intensity={6}>
            <Box sx={{
              borderRadius:"20px", overflow:"hidden",
              border:"1px solid rgba(30,144,255,.15)",
              background:"rgba(4,8,20,.9)",
              boxShadow:"0 20px 60px rgba(0,0,0,.6), 0 0 40px rgba(30,144,255,.1)",
              position:"relative",
            }}>
              {/* Article header bar */}
              <Box sx={{
                background:"linear-gradient(90deg,rgba(30,144,255,.14),rgba(30,144,255,.04))",
                borderBottom:"1px solid rgba(30,144,255,.12)",
                px:{ xs:"18px", md:"24px" }, py:"14px",
                display:"flex", alignItems:"center", gap:"10px",
              }}>
                <Box sx={{ width:8, height:8, borderRadius:"50%", background:"#1E90FF", boxShadow:"0 0 8px #1E90FF" }}/>
                <Typography sx={{ fontFamily:"'DM Sans',sans-serif", fontSize:".72rem", fontWeight:700, color:"rgba(30,144,255,.8)", letterSpacing:".1em", textTransform:"uppercase" }}>
                  Silicon India Magazine
                </Typography>
              </Box>

              {/* Article body preview */}
              <Box sx={{ p:{ xs:"20px 18px", md:"28px 24px" } }}>
                {/* Headline */}
                <Typography sx={{
                  fontFamily:"'Bricolage Grotesque',sans-serif",
                  fontWeight:800,
                  fontSize:"clamp(1rem,2vw,1.4rem)",
                  lineHeight:1.2, mb:"12px",
                  color:"rgba(255,255,255,.9)",
                  letterSpacing:"-.02em",
                }}>
                  Anthrasync: Bridging Language Barriers
                </Typography>

                {/* Body lines simulation */}
                {[100, 92, 88, 65].map((w, i) => (
                  <Box key={i} sx={{
                    height:"8px", borderRadius:"4px", mb:"8px",
                    width:`${w}%`,
                    background:`rgba(255,255,255,${.04 + i*.01})`,
                  }}/>
                ))}

                {/* Divider */}
                <Box sx={{ borderTop:"1px solid rgba(255,255,255,.06)", my:"18px" }}/>

                {/* Quote pull */}
                <Box sx={{
                  borderLeft:"2px solid #1E90FF",
                  pl:"14px", mb:"18px",
                }}>
                  <Typography sx={{
                    fontFamily:"'Bricolage Grotesque',sans-serif",
                    fontWeight:700, fontStyle:"italic",
                    fontSize:"clamp(.82rem,1.1vw,.95rem)",
                    lineHeight:1.55,
                    color:"rgba(210,230,255,.72)",
                  }}>
                    "Bridging Language Barriers to Create a Real-time Knowledge Exchange"
                  </Typography>
                </Box>

                {/* Author row */}
                <Box sx={{ display:"flex", alignItems:"center", gap:"10px" }}>
                  <Box sx={{
                    width:32, height:32, borderRadius:"50%",
                    background:"linear-gradient(135deg,rgba(30,144,255,.4),rgba(30,144,255,.1))",
                    border:"1px solid rgba(30,144,255,.25)",
                    flexShrink:0,
                  }}/>
                  <Box>
                    <Typography sx={{ fontFamily:"'DM Sans',sans-serif", fontSize:".72rem", fontWeight:600, color:"rgba(255,255,255,.7)", lineHeight:1.2 }}>
                      Editorial Feature
                    </Typography>
                    <Typography sx={{ fontFamily:"'DM Sans',sans-serif", fontSize:".62rem", color:"rgba(255,255,255,.3)" }}>
                      Silicon India · Bangalore AI Edition
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </TiltCard>
        </Box>
      </Box>
    </Box>
  </motion.div>
);

/* ─────────────────────── RECOGNITION SECTION ───────────────────── */
const RecognitionSection = ({ label, title, body, certSrc, certAlt, certUrl, btnText, reverse = false, index }) => (
  <motion.div
    initial={{ opacity:0, y:36 }}
    whileInView={{ opacity:1, y:0 }}
    viewport={{ once:true, margin:"-80px" }}
    transition={{ duration:.75, delay: index * .06, ease:[.22,1,.36,1] }}
  >
    <Box sx={{
      position:"relative",
      display:"grid",
      gridTemplateColumns:{ xs:"1fr", lg:"1fr 1fr" },
      gap:{ xs:"40px", md:"56px", lg:"72px", xl:"96px" },
      alignItems:"center",
      // Alternate order
      "& > :first-of-type": { order:{ xs:1, lg: reverse ? 2 : 1 } },
      "& > :last-child":    { order:{ xs:2, lg: reverse ? 1 : 2 } },
    }}>
      {/* Content */}
      <RecogMeta label={label} title={title} body={body}>
        <Button
          variant="contained"
          component={motion.a}
          href={certUrl}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale:1.03 }}
          whileTap={{ scale:.97 }}
          sx={{
            alignSelf:"flex-start",
            background:"linear-gradient(90deg,#1E90FF,#42A5FF)",
            color:"#fff",
            textTransform:"none",
            fontFamily:"'DM Sans',sans-serif",
            fontWeight:700,
            fontSize:"clamp(.84rem,1vw,.94rem)",
            px:"24px", py:"11px",
            borderRadius:"12px",
            boxShadow:"0 10px 30px rgba(30,144,255,.28)",
            border:"1px solid rgba(255,255,255,.08)",
            "&:hover": { background:"linear-gradient(90deg,#42A5FF,#1E90FF)", boxShadow:"0 14px 40px rgba(30,144,255,.4)" },
          }}
        >
          {btnText}
        </Button>
      </RecogMeta>

      {/* Certificate */}
      <Box sx={{ px:{ xs:0, md:"8px", lg:"12px" } }}>
        <CertFrame src={certSrc} alt={certAlt}/>
      </Box>
    </Box>
  </motion.div>
);

/* ─────────────────────── DIVIDER LINE ──────────────────────────── */
const SectionDivider = () => (
  <Box sx={{
    display:"flex", alignItems:"center", gap:"18px",
    my:{ xs:"72px", sm:"88px", md:"112px" },
    opacity:.4,
  }}>
    <Box sx={{ flex:1, height:"1px", background:"linear-gradient(to right,transparent,rgba(30,144,255,.35))" }}/>
    <Box sx={{ width:6, height:6, borderRadius:"50%", background:"rgba(30,144,255,.5)", boxShadow:"0 0 8px rgba(30,144,255,.4)" }}/>
    <Box sx={{ flex:1, height:"1px", background:"linear-gradient(to left,transparent,rgba(30,144,255,.35))" }}/>
  </Box>
);

/* ─────────────────────── HERO ───────────────────────────────────── */
const Hero = () => {
  const container = {
    hidden: {},
    show: { transition: { staggerChildren:.12, delayChildren:.25 } },
  };
  const item = {
    hidden: { opacity:0, y:28, filter:"blur(8px)" },
    show:   { opacity:1, y:0,  filter:"blur(0px)", transition:{ duration:.72, ease:[.22,1,.36,1] } },
  };

  return (
    <Box sx={{
      minHeight:"78vh",
      display:"flex",
      flexDirection:"column",
      justifyContent:"center",
      textAlign:"center",
      pt:{ xs:"120px", md:"140px" },
      pb:{ xs:"60px",  md:"80px" },
      position:"relative",
    }}>
      {/* Hero orb */}
      <Box aria-hidden sx={{
        position:"absolute",
        top:"-20%", left:"50%", transform:"translateX(-50%)",
        width:{ xs:"360px", md:"700px" }, height:{ xs:"260px", md:"500px" },
        borderRadius:"50%",
        background:"radial-gradient(ellipse, rgba(30,144,255,.13) 0%, transparent 70%)",
        filter:"blur(70px)",
        animation:"ach-orb 10s ease-in-out infinite",
        pointerEvents:"none",
      }}/>

      <Container maxWidth="lg" sx={{ position:"relative", zIndex:1, px:{ xs:"20px", md:"40px" } }}>
        <motion.div variants={container} initial="hidden" animate="show">
          {/* Eyebrow */}
          <motion.div variants={item}>
            <StatusBadge text="Recognition & Achievements" />
          </motion.div>

          {/* H1 */}
          <motion.div variants={item}>
            <Typography
              component="h1"
              sx={{
                fontFamily:"'Bricolage Grotesque',sans-serif",
                fontWeight:800,
                fontSize:"clamp(2.4rem,7.5vw,6.5rem)",
                lineHeight:{ xs:1.04, md:1.02 },
                letterSpacing:"clamp(-1.5px,-0.06em,-4px)",
                color:"rgba(255,255,255,.97)",
                mb:{ xs:"12px", md:"16px" },
                wordBreak:"break-word",
              }}
            >
              Recognized By
              <br/>
              <Box component="span" sx={{ color:"#1E90FF" }}>
                Industry Leaders
              </Box>
            </Typography>
          </motion.div>

          {/* Subtitle */}
          <motion.div variants={item}>
            <Typography sx={{
              fontFamily:"'DM Sans',sans-serif",
              fontSize:"clamp(.92rem,1.4vw,1.2rem)",
              lineHeight:1.85,
              color:"rgba(185,210,255,.62)",
              maxWidth:{ xs:"100%", sm:"580px", md:"660px" },
              mx:"auto",
            }}>
              Honored by leading organizations for innovation, entrepreneurship,
              and impactful technology solutions that push enterprise AI forward.
            </Typography>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div variants={item}>
            <Box
              component={motion.div}
              animate={{ y:[0,7,0] }}
              transition={{ duration:2.2, repeat:Infinity, ease:"easeInOut" }}
              sx={{
                mt:"48px",
                width:28, height:44, mx:"auto",
                borderRadius:"14px",
                border:"1px solid rgba(30,144,255,.3)",
                display:"flex", justifyContent:"center", pt:"7px",
                position:"relative",
              }}
            >
              <Box sx={{
                width:3, height:8,
                borderRadius:"2px",
                background:"rgba(30,144,255,.7)",
              }}/>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

/* ─────────────────────── QUOTE SECTION ─────────────────────────── */
const QuoteSection = () => (
  <motion.div
    initial={{ opacity:0 }}
    whileInView={{ opacity:1 }}
    viewport={{ once:true, margin:"-60px" }}
    transition={{ duration:.9, ease:[.22,1,.36,1] }}
  >
    <Box sx={{
      position:"relative",
      textAlign:"center",
      py:{ xs:"72px", sm:"88px", md:"112px" },
      px:{ xs:"20px", sm:"40px" },
    }}>
      {/* Large quote mark */}
      <Typography aria-hidden sx={{
        fontFamily:"'Bricolage Grotesque',sans-serif",
        fontSize:"clamp(5rem,12vw,10rem)",
        lineHeight:.7,
        color:"rgba(30,144,255,.08)",
        mb:"-20px",
        userSelect:"none",
      }}>
        "
      </Typography>

      <Typography sx={{
        fontFamily:"'Bricolage Grotesque',sans-serif",
        fontWeight:700,
        fontSize:"clamp(1.4rem,3.2vw,2.8rem)",
        lineHeight:1.35,
        letterSpacing:"clamp(-.8px,-0.03em,-1.4px)",
        color:"rgba(255,255,255,.88)",
        maxWidth:{ xs:"100%", md:"880px" },
        mx:"auto",
        mb:"28px",
      }}>
        These recognitions represent a commitment to innovation,
        technology excellence, and building solutions that create
        meaningful impact.
      </Typography>

      {/* Divider line */}
      <Box sx={{
        width:"60px", height:"2px", mx:"auto",
        background:"linear-gradient(90deg,transparent,#1E90FF,transparent)",
        borderRadius:"1px",
      }}/>
    </Box>
  </motion.div>
);

/* ─────────────────────── CTA SECTION ───────────────────────────── */
const CTASection = () => (
  <motion.div
    initial={{ opacity:0, y:28 }}
    whileInView={{ opacity:1, y:0 }}
    viewport={{ once:true }}
    transition={{ duration:.7, ease:[.22,1,.36,1] }}
  >
    <Box sx={{
      position:"relative",
      textAlign:"center",
      pb:{ xs:"96px", md:"128px" },
      px:{ xs:"20px", sm:"40px" },
    }}>
      {/* CTA glow */}
      <Box aria-hidden sx={{
        position:"absolute", bottom:"-5%", left:"50%", transform:"translateX(-50%)",
        width:{ xs:"280px", md:"500px" }, height:{ xs:"200px", md:"360px" },
        borderRadius:"50%",
        background:"radial-gradient(ellipse,rgba(30,144,255,.1),transparent 70%)",
        filter:"blur(70px)", pointerEvents:"none",
      }}/>

      <Typography sx={{
        fontFamily:"'Bricolage Grotesque',sans-serif",
        fontWeight:800,
        fontSize:"clamp(2rem,5.5vw,4.5rem)",
        lineHeight:1.06,
        letterSpacing:"clamp(-1.2px,-0.05em,-2.4px)",
        color:"rgba(255,255,255,.97)",
        mb:"32px",
        wordBreak:"break-word",
        position:"relative", zIndex:1,
      }}>
        The Journey Has
        <Box component="span" sx={{ color:"#1E90FF" }}> Just Started</Box>
      </Typography>

      <Button
        variant="contained"
        size="large"
        component={motion.button}
        whileHover={{ scale:1.04 }}
        whileTap={{ scale:.96 }}
        sx={{
          position:"relative", zIndex:1,
          background:"linear-gradient(135deg,#1565C0 0%,#1E90FF 100%)",
          color:"#fff",
          textTransform:"none",
          fontFamily:"'DM Sans',sans-serif",
          fontWeight:700,
          fontSize:"clamp(.92rem,1.2vw,1.05rem)",
          px:"36px", py:"14px",
          borderRadius:"14px",
          boxShadow:"0 14px 40px rgba(30,144,255,.32)",
          "&:hover": {
            background:"linear-gradient(135deg,#1976D2 0%,#42A5FF 100%)",
            boxShadow:"0 20px 52px rgba(30,144,255,.48)",
          },
        }}
      >
        Explore Portfolio
      </Button>
    </Box>
  </motion.div>
);

/* ─────────────────────── PAGE ───────────────────────────────────── */
export default function Achievements() {
  return (
    <>
      <Styles />
      <Header1/>

      {/* Fixed background layers */}
      <Box aria-hidden sx={{
        position:"fixed", inset:0,
        background:"radial-gradient(ellipse 70% 50% at 50% 0%, rgba(30,144,255,.09) 0%, transparent 70%), #050816",
        zIndex:-2,
      }}/>
      <Box className="ach-grain" aria-hidden/>
      <ParticleField/>

      {/* Content */}
      <Box sx={{ position:"relative", zIndex:1, minHeight:"100vh" }}>

        {/* ── HERO ── */}
        <Hero/>

        {/* ── RECOGNITION SECTIONS ── */}
        <Container
          maxWidth="xl"
          sx={{
            position:"relative", zIndex:2,
            px:{ xs:"20px", sm:"28px", md:"48px", lg:"64px", xl:"80px" },
            maxWidth:{ xl:"1440px" },
          }}
        >

          {/* Startup India — Image Right */}
          <RecognitionSection
            index={0}
            label="Startup India · DPIIT"
            title={<>Certificate of<Box component="span" sx={{ color:"#1E90FF" }}> Recognition</Box></>}
            body="Official recognition from DPIIT under Startup India, acknowledging our innovation, entrepreneurship, and technology-driven impact in the enterprise AI space."
            certSrc={CertificateImage}
            certAlt="Startup India Certificate"
            certUrl="https://recognition-be.startupindia.gov.in/s3/download/document/RECOGNITION_CERTIFICATE/4b32be77-1e45-4d03-b539-315ea21a4c55.pdf"
            btnText="View Certificate →"
            reverse={false}
          />

          <SectionDivider/>

          {/* Startup Karnataka — Image Left */}
          <RecognitionSection
            index={1}
            label="Startup Karnataka"
            title={<>Registered<Box component="span" sx={{ color:"#1E90FF" }}> Startup</Box></>}
            body="Officially recognized under the Karnataka Startup ecosystem for building innovative AI products and technology solutions that advance enterprise automation."
            certSrc={Startup_Karnataka_Registration}
            certAlt="Startup Karnataka Certificate"
            certUrl="/Startup_Karnataka_Registration.pdf"
            btnText="View Registration →"
            reverse={true}
          />

          <SectionDivider/>

          {/* Silicon India — Magazine layout */}
          <Box sx={{ mb:{ xs:"72px", md:"88px" } }}>
            <Box sx={{ textAlign:"center", mb:{ xs:"36px", md:"48px" } }}>
              <SectionLabel text="Silicon India · Media" />
              <Typography component="h2" sx={{
                fontFamily:"'Bricolage Grotesque',sans-serif",
                fontWeight:800,
                fontSize:"clamp(2rem,4.5vw,3.8rem)",
                lineHeight:1.06,
                letterSpacing:"clamp(-1.2px,-0.05em,-2px)",
                color:"rgba(255,255,255,.97)",
                wordBreak:"break-word",
              }}>
                Featured<Box component="span" sx={{ color:"#1E90FF" }}> Recognition</Box>
              </Typography>
            </Box>
            <MagazineFeature/>
          </Box>

        </Container>

        {/* ── QUOTE ── */}
        <Box sx={{ position:"relative", zIndex:2 }}>
          <GlowOrb top="50%" left="50%" w={600} h={400} opacity={.07}/>
          <Container maxWidth="xl" sx={{ px:{ xs:"20px", md:"48px" }, maxWidth:{ xl:"1440px" } }}>
            <QuoteSection/>
          </Container>
        </Box>

        {/* ── CTA ── */}
        {/* <Container maxWidth="xl" sx={{ position:"relative", zIndex:2, px:{ xs:"20px", md:"48px" }, maxWidth:{ xl:"1440px" } }}>
          <CTASection/>
        </Container> */}


      </Box>
      <Footer/>
    </>
  );
}